/**
 * Maker-checker UI gating for V2 approval workflows
 *
 * Single source of truth for which action buttons (Edit, Submit, Approve,
 * Return, Reopen, Mark Completed, Cancel) are visible on the detail pages of
 * entities that follow the same DRAFT → SUBMITTED → ACTIVE/APPROVED →
 * COMPLETED/CANCELLED lifecycle (Contract, Work Plan, Spend Plan, etc.).
 *
 * The backend (see bldt-ui-fn/common/services/makerChecker.ts) enforces these
 * rules authoritatively — this module only mirrors them so the UI never
 * exposes an action the user cannot perform.
 *
 * Status semantics by entity:
 *   - Contract / Work Plan: DRAFT → SUBMITTED → ACTIVE → COMPLETED|CANCELLED
 *   - Spend Plan:           DRAFT → SUBMITTED → APPROVED → COMPLETED
 *
 * "Approved" is normalised so callers don't have to care whether the post-
 * approval status string is ACTIVE or APPROVED.
 */

import type { Permissions } from '../@types';

export type MakerCheckerStatus =
	| 'DRAFT'
	| 'SUBMITTED'
	| 'ACTIVE'
	| 'APPROVED'
	| 'COMPLETED'
	| 'CANCELLED'
	| 'LOCKED'
	| string;

/** Permission resource keys on the Permissions object (camelCase). */
export type MakerCheckerResource = 'contract' | 'workPlan' | 'spendPlan' | 'invoice' | 'voucher';

export interface MakerCheckerInput {
	/** Current entity status. */
	status: MakerCheckerStatus | null | undefined;
	/** The user who created/submitted the entity (the "maker"). */
	makerUserId?: number | null;
	/** The id of the user currently viewing the page. */
	currentUserId?: number | null;
	/** Project-scoped permissions for the current user. */
	permissions?: Permissions | null | undefined;
	/** Permission resource key, e.g. `'contract'`, `'workPlan'`. */
	resource: MakerCheckerResource;
}

// Status predicates

export const isDraft = (status?: string | null): boolean => status === 'DRAFT';

export const isPendingApproval = (status?: string | null): boolean => status === 'SUBMITTED';

/**
 * Post-approval / live state. Returns true for either ACTIVE (Contract,
 * Work Plan) or APPROVED (Spend Plan). Contract rows are constrained by
 * the DB to ACTIVE for this state — the APPROVED branch only fires for
 * Spend Plan callers using the shared helper.
 */
export const isApproved = (status?: string | null): boolean =>
	status === 'ACTIVE' || status === 'APPROVED';

export const isTerminal = (status?: string | null): boolean =>
	status === 'COMPLETED' || status === 'CANCELLED';

// Action gates
//
// Each helper takes the same MakerCheckerInput so callers don't have to
// remember which inputs apply to which check. Missing inputs degrade safely
// to "not allowed".

const hasUpdate = (input: MakerCheckerInput): boolean =>
	!!input.permissions?.[input.resource]?.update;

const hasApprove = (input: MakerCheckerInput): boolean =>
	!!input.permissions?.[input.resource]?.approve;

const isMaker = (input: MakerCheckerInput): boolean =>
	!!input.makerUserId && !!input.currentUserId && input.makerUserId === input.currentUserId;

/**
 * Mirror of the backend `isMakerCheckerEnforced()` helper (see
 * bldt-ui-fn/common/utils/connectors.ts). Enforced by default in every
 * environment. The ONLY way to relax it — letting the maker also approve their
 * own submission so a single developer can run the full DRAFT → SUBMITTED →
 * APPROVED flow end-to-end — is to explicitly set
 * `REACT_APP_ENFORCE_MAKER_CHECKER=false` (done in `.env.local`, `.env.develop`
 * and `.env.develop-v2`). QA, prod and every other environment leave it unset
 * and therefore enforce. The backend must also relax `FF_ENFORCE_MAKER_CHECKER`
 * for the approve call to actually succeed — this only affects button visibility.
 */
const enforceSeparation = (): boolean => process.env.REACT_APP_ENFORCE_MAKER_CHECKER !== 'false';

/**
 * Edit is allowed only while the entity is in DRAFT, and only for users
 * with the entity's `update` permission. Once SUBMITTED/ACTIVE/APPROVED/
 * COMPLETED/CANCELLED the entity is locked and any change must go back
 * through the review/approval workflow by first returning it to DRAFT.
 */
export const canEdit = (input: MakerCheckerInput): boolean =>
	isDraft(input.status) && hasUpdate(input);

/** Submit for approval — DRAFT only, requires `update` permission. */
export const canSubmit = (input: MakerCheckerInput): boolean =>
	isDraft(input.status) && hasUpdate(input);

/**
 * Approve — SUBMITTED only, requires `approve` permission, and enforces
 * separation of duties (the maker cannot also be the checker).
 */
export const canApprove = (input: MakerCheckerInput): boolean =>
	isPendingApproval(input.status) && hasApprove(input) && (!enforceSeparation() || !isMaker(input));

/**
 * Return / Reject — SUBMITTED only. Either the checker (with `approve`)
 * sends it back during review, or the maker (with `update`) recalls their
 * own pending submission. Once APPROVED, the path back to DRAFT is
 * "Reopen", not "Reject" — rejecting an already-approved plan is
 * semantically wrong and the audit trail should reflect a reopen.
 */
export const canReturn = (input: MakerCheckerInput): boolean =>
	isPendingApproval(input.status) && (hasApprove(input) || hasUpdate(input));

/**
 * Reopen back to DRAFT from a terminal/approved state so the maker can
 * correct mistakes and resubmit through the maker-checker workflow.
 * Requires `update` permission. By default only available from terminal
 * states (COMPLETED/CANCELLED); pass `fromApproved` to also allow
 * reopening from ACTIVE/APPROVED.
 */
export const canReopen = (
	input: MakerCheckerInput,
	{ fromApproved = false }: { fromApproved?: boolean } = {}
): boolean => {
	if (!hasUpdate(input)) return false;
	if (isTerminal(input.status)) return true;
	if (fromApproved && isApproved(input.status)) return true;
	return false;
};

/** Mark as completed — only from the live state, requires `update`. */
export const canComplete = (input: MakerCheckerInput): boolean =>
	isApproved(input.status) && hasUpdate(input);

/** Cancel — only from the live state, requires `update`. */
export const canCancel = (input: MakerCheckerInput): boolean =>
	isApproved(input.status) && hasUpdate(input);
